/**
 * The service offerings, keyed by URL slug. UAE e-invoicing integration is a
 * specialism of custom operations software with its own page, not a seventh
 * discipline - the /services hub presents it under operations.
 *
 * Used for blog → service internal linking. Deep links from editorial content
 * into the commercial pages are how a blog passes relevance to the pages that
 * actually need to rank, so this list has to stay in step with the routes in
 * src/index.js and route-config.js.
 *
 * `banner` is the copy for the call-to-action banner at the top of every
 * article (see BlogServiceBanner). Its points restate what each service page
 * already promises in its hero - don't add a claim here the page doesn't make.
 */
import { aed, E_INVOICING } from './pricing';

export const services = {
  'web-mobile-development': {
    name: 'Web & Mobile Development',
    path: '/services/web-mobile-development',
    blurb: 'Websites and apps built for speed, conversion and full code ownership.',
    banner: {
      title: 'Need a website or app that turns visitors into customers?',
      points: ['Built mobile-first for speed', 'Fixed-cost proposals', 'You own the code, with support after launch'],
      cta: 'Explore web & mobile development',
    },
  },
  'ai-automation': {
    name: 'AI & Automation',
    path: '/services/ai-automation',
    blurb: 'AI wired into the tools you already run - audited before it is built.',
    banner: {
      title: 'Put AI to work on the workflows you already run',
      points: ['Audit before anything is built', 'Proven on your own data first', 'Your data stays yours, full IP ownership'],
      cta: 'Explore AI & automation',
    },
  },
  'custom-operations-software': {
    name: 'Custom Operations Software',
    path: '/services/custom-operations-software',
    blurb: 'Internal systems that replace spreadsheets and per-seat SaaS licences.',
    banner: {
      title: 'Run your operation on software built around it',
      points: ['Integrates with your existing stack', 'No per-seat licences, you own the source', 'Phased, low-risk rollout'],
      cta: 'Explore custom operations software',
    },
  },
  'embedded-systems': {
    name: 'Embedded Systems & IoT',
    path: '/services/embedded-systems',
    blurb: 'Firmware, RTOS and secure OTA updates for devices that ship.',
    banner: {
      title: 'Firmware and IoT built to work in the field',
      points: ['Firmware & RTOS development', 'Secure over-the-air updates', 'Tested on real hardware, full IP ownership'],
      cta: 'Explore embedded systems',
    },
  },
  'medical-software-development': {
    name: 'Medical Software Development',
    path: '/services/medical-software-development',
    blurb: 'Clinical platforms for UAE providers, EHR and HL7/FHIR integrations.',
    banner: {
      title: 'Clinical software built around how care is delivered',
      points: ['Built to UAE health data law', 'HL7 & FHIR native integrations', 'Audit-ready documentation, full IP ownership'],
      cta: 'Explore medical software development',
    },
  },
  'uae-e-invoicing-integration': {
    name: 'UAE E-Invoicing Integration',
    path: '/services/uae-e-invoicing-integration',
    blurb: 'Your ERP or accounting system connected to your ASP, with PINT AE data validated before it is sent.',
    banner: {
      // Non-breaking hyphen (U+2011): phones otherwise wrap it as "UAE e-" / "invoicing".
      title: 'Get your systems ready for UAE e‑invoicing',
      points: ['Connect your ERP or accounting system to your ASP', 'PINT AE data mapping and validation before submission', `Data gap assessment ${aed(E_INVOICING.assessment)}, then a fixed-price build`],
      cta: 'Explore e-invoicing integration',
    },
  },
  'generative-engine-optimization': {
    name: 'Generative Engine Optimization',
    path: '/services/generative-engine-optimization',
    blurb: 'Get named and cited when buyers ask ChatGPT, Gemini or Perplexity.',
    banner: {
      title: 'Get your brand named when buyers ask AI',
      points: ['Audit before commitment', 'Fixed-cost proposals, you own every asset', 'Monthly citation reporting'],
      cta: 'Explore GEO services',
    },
  },
};

export const serviceList = Object.entries(services).map(([slug, s]) => ({ slug, ...s }));
